import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { SelfUserStore } from '@domains/person/user/self/data';
import { TOwnerReportHeader } from '../entities/owner-report-header.model';
import { OwnerProfileService } from '../infra/owner-profile.service';

type TOwnerReportHeaderState = {
  /** The session's tenant when the header was asked: the answer is its. */
  tenantId: string | null;
  /** Null while loading and after a failed load. */
  loadedHeader: TOwnerReportHeader | null;
};

/**
 * The line under the gym's name in exported PDFs. Kept apart from
 * `LogosStore`, which reads the accountservice and raises every failure: this
 * one is a nicety, so a failure stays silent and the PDF keeps the header it
 * had before (name and logo).
 */
export const OwnerReportHeaderStore = signalStore(
  { providedIn: 'root' },
  withState<TOwnerReportHeaderState>({ tenantId: null, loadedHeader: null }),
  withReqState('loadReportHeader'),
  withComputed((store, selfUserStore = inject(SelfUserStore)) => ({
    /**
     * The header of the session's tenant. Null when it was loaded for another
     * one: the mobile app logs out without reloading, so the next login may be
     * another gym's, and its PDF must not carry the previous gym's CNPJ.
     */
    header: computed(() =>
      store.tenantId() === (selfUserStore.userProfile()?.TenantId ?? null)
        ? store.loadedHeader()
        : null
    ),
  })),
  withMethods(
    (
      store,
      service = inject(OwnerProfileService),
      selfUserStore = inject(SelfUserStore)
    ) => {
      const load = rxMethod<string | null>(
        pipe(
          tap((tenantId) =>
            patchState(
              store,
              { tenantId, loadedHeader: null },
              setPropLoading('loadReportHeader')
            )
          ),
          switchMap(() =>
            service.getReportHeader().pipe(
              tapResponse({
                next: (loadedHeader) =>
                  patchState(
                    store,
                    { loadedHeader },
                    setPropLoaded('loadReportHeader')
                  ),
                error: (error: unknown) =>
                  patchState(store, setPropError('loadReportHeader', error)),
              })
            )
          )
        )
      );

      return {
        /**
         * Loads once per tenant. A failed load leaves `loadReportHeaderLoaded`
         * false, so the next export tries again.
         */
        ensureLoaded(): void {
          const tenantId = selfUserStore.userProfile()?.TenantId ?? null;
          if (
            store.tenantId() === tenantId &&
            (store.loadReportHeaderLoaded() || store.loadReportHeaderLoading())
          ) {
            return;
          }
          load(tenantId);
        },
      };
    }
  )
);
